<template>
  <BaseLayout>
    <template #content>
      <div
        class="sites-page bg-soft min-vh-100 py-3"
        :data-admin-theme="adminThemeKey"
        :style="adminThemeStyle"
      >
        <!-- HEADER -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 class="h4 mb-1">Sites / Boutiques ({{ totalRows }})</h1>
            <small class="text-muted">
              Gérez vos différents magasins (multi-sites). Un site = une boutique avec ses propres paramètres (TVA, CSS, fuseau horaire...).
            </small>
            <div v-if="isMultiSiteAdmin" class="small mt-1" :class="hasReachedShopLimit ? 'text-danger fw-semibold' : 'text-muted'">
              Forfait : {{ totalRows }} / {{ forfaitMaxShops }} boutiques
            </div>
          </div>

          <div class="d-flex gap-2">
            <ModuleActivityButton :module="['shops', 'ShopSetting']" label="Sites" />

            <button
              type="button"
              class="btn btn-outline-secondary btn-sm btn-icon-label"
              :disabled="isLoading"
              title="Actualiser la liste"
              aria-label="Actualiser la liste"
              @click="fetchSites"
            >
              <i class="fa fa-rotate-right"></i>
              <span class="btn-label ms-1">Actualiser</span>
            </button>

            <router-link
              v-if="isMultiSiteAdmin || (isGlobalUser && canViewProducts)"
              class="btn btn-outline-primary btn-sm btn-icon-label"
              :to="{ name: 'AllSitesProductDatabase' }"
              title="Base de donnée produits de tous les sites"
            >
              <i class="fa fa-database"></i>
              <span class="btn-label ms-1">Base de donnée</span>
            </router-link>

            <router-link
              v-if="isMultiSiteAdmin"
              class="btn btn-outline-primary btn-sm btn-icon-label"
              :to="{ name: 'AllSitesUsers' }"
              title="Utilisateurs de tous les sites"
            >
              <i class="fa fa-users"></i>
              <span class="btn-label ms-1">Utilisateurs</span>
            </router-link>

            <button
              v-if="isMultiSiteAdmin"
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon-label"
              :disabled="isLoading && !hasLoadedSites"
              title="Paramètres globaux"
              aria-label="Paramètres globaux"
              @click="openBrandSettingsDialog"
            >
              <i class="fa fa-gear"></i>
            </button>
          </div>
        </div>

        <!-- MESSAGES -->
        <div v-if="errorMessage" class="alert alert-danger py-2">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="alert alert-success py-2">
          {{ successMessage }}
        </div>
        <div v-if="shopLimitAlertVisible" class="alert alert-warning alert-dismissible py-2">
          La limite de {{ forfaitMaxShops }} boutiques de votre forfait est atteinte. Supprimez une boutique ou augmentez votre forfait pour en créer une nouvelle.
          <button
            type="button"
            class="btn-close py-2"
            aria-label="Fermer"
            @click="shopLimitAlertVisible = false"
          ></button>
        </div>

        <!-- LOADING -->
        <div v-if="isLoading" class="text-center my-5">
          <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
          <div class="small text-muted">Chargement des sites...</div>
        </div>

        <!-- CARDS -->
        <div v-else>
          <div class="d-flex justify-content-center">
            <div class="sites-cards-wrap">
              <div class="row g-3">
                <div v-if="isGerant" class="col-md-6 col-lg-4">
                  <button
                    type="button"
                    class="card new-site-card h-100 w-100"
                    :class="{ 'new-site-card-disabled': hasReachedShopLimit }"
                    :aria-disabled="hasReachedShopLimit ? 'true' : 'false'"
                    :title="hasReachedShopLimit ? 'Limite du forfait atteinte' : 'Créer une nouvelle boutique'"
                    @click="openCreateDialog"
                  >
                    <span class="new-site-card-icon">
                      <i class="fa fa-plus"></i>
                    </span>
                    <span class="new-site-card-title">Nouveau site</span>
                    <span class="new-site-card-text">
                      {{ sitesLocal.length ? 'Créer une nouvelle boutique.' : 'Créez votre première boutique.' }}
                    </span>
                  </button>
                </div>

                <div class="col-md-6 col-lg-4" v-for="site in sitesLocal" :key="site.id">
                  <div
                    class="card shadow-sm site-card h-100"
                    :class="{ 'site-card-current': isCurrentSite(site) }"
                  >
                    <a
                      class="site-card-link"
                      :href="siteShowHref(site)"
                      :aria-label="isGlobalUser && canSwitchSite ? `Se connecter au site ${site.name || site.id}` : `Voir le site ${site.name || site.id}`"
                      :aria-disabled="isGlobalUser && !canSwitchSite ? 'true' : undefined"
                      @click.prevent="goToShow(site)"
                      @keydown.enter.prevent="goToShow(site)"
                    >
                      <div class="site-cover" :class="siteCoverClass(site)">
                      <img
                        v-if="site.cover_image_url"
                        :src="site.cover_image_url"
                        :alt="`Couverture ${site.name || 'site'}`"
                        class="site-cover-image"
                      />
                      <div v-else class="site-cover-initials">
                        {{ siteInitials(site) }}
                      </div>
                      <span class="site-type-badge" :class="siteTypeBadgeClass(site)">
                        {{ siteTypeLabel(site.site_type) }}
                      </span>
                      </div>

                      <div class="card-body d-flex flex-column">
                      <!-- top -->
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="me-2">
                          <div class="fw-bold mb-1 d-flex align-items-center gap-2">
                            <span>{{ site.name }}</span>
                          </div>

                          <small v-if="site.legal_name" class="text-muted d-block">
                            {{ site.legal_name }}
                          </small>

                          <small class="text-muted d-block">
                            <span v-if="site.code" class="badge bg-light text-muted me-1">{{ site.code }}</span>
                            <span>{{ site.city || '-' }}</span>
                            <span v-if="site.country" class="opacity-75"> · {{ site.country }}</span>
                          </small>

                          <!-- ✅ slogan -->
                          <small v-if="site.slogan" class="text-muted d-block mt-1">
                            <i class="fa fa-quote-left me-1 opacity-50"></i>
                            {{ site.slogan }}
                          </small>
                        </div>

                        <div class="d-flex flex-column align-items-end gap-2">
                          <span class="badge" :class="site.is_active ? 'bg-success' : 'bg-secondary'">
                            {{ site.is_active ? 'Actif' : 'Inactif' }}
                          </span>
                        </div>
                      </div>

                      <hr class="my-3" />

                      <!-- infos -->
                      <div class="site-card-facts">
                        <div class="d-flex justify-content-between mb-1">
                          <span class="text-muted">WhatsApp</span>
                          <span class="fw-semibold">{{ site.whatsapp_phone || '-' }}</span>
                        </div>
                        <div class="d-flex justify-content-between mb-1">
                          <span class="text-muted">Fuseau</span>
                          <span class="fw-semibold">{{ site.timezone || '-' }}</span>
                        </div>
                        <div class="d-flex justify-content-between gap-3 mb-1">
                          <span><span class="text-muted">TVA</span> <strong>{{ formatRatePercentage(site.tva_rate) }}</strong></span>
                          <span><span class="text-muted">CSS</span> <strong>{{ formatRatePercentage(site.css_rate) }}</strong></span>
                        </div>
                        <div class="d-flex justify-content-between mb-1">
                          <span class="text-muted">Produits actifs</span>
                          <span class="fw-semibold">{{ formatNumber(site.products_active_count || 0) }}</span>
                        </div>
                        <div class="d-flex justify-content-between mb-1">
                          <span class="text-muted">Chiffre d’affaires</span>
                          <span class="fw-semibold">{{ formatCurrency(site.validated_sales_revenue || 0) }}</span>
                        </div>
                      </div>

                      </div>
                    </a>

                    <div class="card-footer site-card-footer">
                      <div class="d-flex flex-wrap gap-2">
                          <!-- ✅ définir site principal -->
                            <button
                              v-if="isGerant && !isMainSite(site)"
                              type="button"
                              class="btn btn-dark btn-sm btn-action-icon"
                              @click="setMainSite(site)"
                              :disabled="isLoading || !site.is_active"
                              title="Définir ce site comme site principal"
                            >
                              <i class="fa fa-crown"></i>
                              <span class="btn-label ms-1">Définir principal</span>
                            </button>

                          <button
                              v-if="canSwitchSite && !isCurrentSite(site)"
                              type="button"
                              class="btn btn-primary btn-sm btn-action-icon"
                              @click="switchToSite(site)"
                              :disabled="isLoading || !site.is_active"
                              title="Se connecter à ce site"
                            >
                              <i class="fa fa-exchange-alt"></i>
                              <span class="btn-label ms-1">Se connecter</span>
                            </button>

                            <button
                              v-if="!isGlobalUser"
                              type="button"
                              class="btn btn-outline-secondary btn-sm btn-action-icon"
                              title="Voir"
                              aria-label="Voir"
                              @click="goToShow(site)"
                            >
                              <i class="fa fa-eye"></i>
                              <span class="btn-label ms-1">Voir</span>
                            </button>
                      </div>

                      <small v-if="isGerant && isMainSite(site)" class="text-muted d-block mt-2">
                        <i class="fa fa-info-circle me-1"></i>
                        Seul le site principal porte le logo global de la marque.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- MODAL PARAMETRES GLOBAUX -->
        <PrimevueDialog
          v-model:visible="brandSettingsDialogVisible"
          :modal="true"
          :style="{ width: '920px', maxWidth: '96vw' }"
          header="Identité de marque"
        >
          <form
            v-if="hasLoadedSites || mainSite"
            class="brand-settings-form"
            @submit.prevent="saveBrandSettings"
          >
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
              <div>
                <h2 class="h6 mb-1">
                  <i class="fa fa-store me-1"></i>
                  Identité de marque
                </h2>
                <small class="text-muted">
                  Logo, nom principal, slogan, langue et plan comptable de la marque.
                </small>
              </div>

              <span v-if="mainSite" class="badge bg-dark">
                <i class="fa fa-crown me-1"></i>
                {{ mainSite.name }}
              </span>
            </div>

            <div v-if="mainSite" class="row g-3 align-items-stretch">
              <div class="col-lg-4">
                <label class="form-label small fw-semibold">Logo de la marque</label>
                <div class="brand-logo-panel mb-2">
                  <img
                    v-if="brandLogoSrc"
                    :src="brandLogoSrc"
                    alt="Logo de la marque"
                    class="brand-logo-preview"
                  />
                  <div v-else class="brand-logo-placeholder">
                    <i class="fa fa-image"></i>
                  </div>
                </div>

                <input
                  ref="brandLogoInput"
                  type="file"
                  class="form-control form-control-sm"
                  accept="image/*"
                  :disabled="isSavingBrand"
                  @change="onBrandLogoSelected"
                />
                <button
                  v-if="brandLogoPreviewUrl"
                  type="button"
                  class="btn btn-outline-secondary btn-sm mt-2"
                  :disabled="isSavingBrand"
                  @click="clearBrandLogoSelection"
                >
                  <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                  Annuler le fichier
                </button>
              </div>

              <div class="col-lg-8">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">Nom principal de la marque *</label>
                    <input
                      v-model.trim="brandForm.name"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Ex: Suite241"
                      required
                      :disabled="isSavingBrand"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">Slogan</label>
                    <input
                      v-model.trim="brandForm.slogan"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Ex: La qualité au meilleur prix"
                      :disabled="isSavingBrand"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">Langue</label>
                    <select
                      v-model="brandForm.language"
                      class="form-select form-select-sm"
                      :disabled="true"
                    >
                      <option value="fr">Français</option>
                    </select>
                    <small class="text-muted">Seul le français est pris en compte pour l’instant.</small>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">Plan comptable</label>
                    <select
                      v-model="brandForm.accounting_plan"
                      class="form-select form-select-sm"
                      :disabled="isSavingBrand || accountingPlans.length <= 1"
                    >
                      <option
                        v-for="plan in accountingPlans"
                        :key="plan.code"
                        :value="plan.code"
                      >
                        {{ plan.name }}
                      </option>
                    </select>
                    <small class="text-muted">Seul plan pris en compte pour le moment.</small>
                  </div>
                </div>

                <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                  <small class="text-muted">
                    Ces informations s’appliquent au site principal.
                  </small>
                  <button type="submit" class="btn btn-primary btn-sm" :disabled="isSavingBrand || isLoading">
                    <span v-if="isSavingBrand">
                      <i class="fa fa-spinner fa-spin me-1"></i>
                      Enregistrement...
                    </span>
                    <span v-else>
                      <i class="fa fa-save me-1"></i>
                      Enregistrer la marque
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="alert alert-info py-2 mb-0">
              Définissez d’abord un site principal pour gérer l’identité de marque.
            </div>
          </form>

          <div v-else class="text-center py-4">
            <i class="fa fa-spinner fa-spin mb-2"></i>
            <div class="small text-muted">Chargement des paramètres globaux...</div>
          </div>
        </PrimevueDialog>

        <!-- MODAL FORMULAIRE -->
        <PrimevueDialog
          v-model:visible="dialogVisible"
          :modal="true"
          :style="{ width: '720px', maxWidth: '100%' }"
          :closable="false"
        >
          <template #header>
            <h5 class="mb-0">Nouveau site</h5>
          </template>

          <SiteForm
            mode="create"
            :default-ecommerce-enabled="!mainSite"
            @saved="handleSaved"
            @cancel="closeDialog"
          />
        </PrimevueDialog>
      </div>
    </template>
  </BaseLayout>
</template>

<script>
import SiteForm from './SiteForm.vue';
import {
  adminThemeStyle as buildAdminThemeStyle,
  getAdminTheme,
} from '@/support/adminThemes';
import { extractApiList } from '@/support/moduleFilters';
import { hasPermission, isSupportUser } from '@/support/permissions';

const BRAND_SETTINGS_STORAGE_KEY = 'suite241.brandSettings';
const SYSCOHADA_REVISED_CODE = 'syscohada_revise';
const DEFAULT_ACCOUNTING_PLAN = {
  code: SYSCOHADA_REVISED_CODE,
  name: 'SYSCOHADA révisé',
};

export default {
  name: 'SitesView',
  components: { SiteForm },
  data() {
    return {
      isLoading: false,
      hasLoadedSites: false,
      errorMessage: '',
      successMessage: '',
      shopLimitAlertVisible: false,
      hasProcessedShopLimitNotice: false,
      dialogVisible: false,
      brandSettingsDialogVisible: false,
      sitesLocal: [],
      isSavingBrand: false,
      accountingPlans: [DEFAULT_ACCOUNTING_PLAN],
      brandLogoFile: null,
      brandLogoPreviewUrl: '',
      brandForm: {
        name: '',
        slogan: '',
        language: 'fr',
        accounting_plan: SYSCOHADA_REVISED_CODE,
        logo_url: '',
      },
    };
  },
  computed: {
    mainSite() {
      return (this.sitesLocal || []).find((site) => this.isMainSite(site)) || null;
    },
    totalRows() {
      return this.sitesLocal.length;
    },
    forfaitMaxShops() {
      return this.$store.getters['runtime/forfaitMaxShops'];
    },
    hasReachedShopLimit() {
      return this.totalRows >= this.forfaitMaxShops;
    },
    brandLogoSrc() {
      return this.brandLogoPreviewUrl || this.brandForm.logo_url || '';
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    isGerant() {
      return this.currentUser && this.currentUser.role === 'gerant';
    },
    isMultiSiteAdmin() {
      return ['gerant', 'superadmin', 'super_admin'].includes(String(this.currentUser?.role || ''));
    },
    isSupport() {
      return isSupportUser(this.currentUser);
    },
    isSuperadmin() {
      return ['superadmin', 'super_admin'].includes(String(this.currentUser?.role || ''));
    },
    canSwitchSite() {
      return this.isMultiSiteAdmin || this.isSupport || (this.isGlobalUser && this.canSwitchShops);
    },
    isGlobalUser() {
      return this.currentUser?.role === 'utilisateur' && !this.currentUser?.shop_id;
    },
    canViewProducts() {
      return hasPermission(this.currentUser, 'products.view');
    },
    canSwitchShops() {
      return hasPermission(this.currentUser, 'shops.switch');
    },
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    userSettings() {
      return this.$store.getters['userSettings/settings'] || {};
    },
    adminTheme() {
      return getAdminTheme(this.userSettings?.appearance?.admin_theme);
    },
    adminThemeKey() {
      return this.adminTheme.key;
    },
    adminThemeStyle() {
      return buildAdminThemeStyle(this.adminThemeKey);
    },
  },
  watch: {
    sitesLocal() {
      this.syncBrandFormFromMainSite();
    },
  },
  mounted() {
    this.fetchAccountingPlans();
    this.fetchSites();
  },
  beforeUnmount() {
    this.revokeBrandLogoPreview();
  },
  methods: {
    async fetchSites() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get('/shops');
        this.sitesLocal = extractApiList(res.data);
        await this.handleShopLimitNotice();
      } catch (error) {
        console.error('Erreur chargement sites', error);
        this.errorMessage = 'Impossible de charger la liste des sites.';
      } finally {
        this.isLoading = false;
        this.hasLoadedSites = true;
      }
    },
    async handleShopLimitNotice() {
      if (!this.isGerant || !this.hasReachedShopLimit || this.hasProcessedShopLimitNotice) return;

      this.hasProcessedShopLimitNotice = true;
      const alreadySeen = this.userSettings?.notices?.shop_limit_reached_seen === true;
      if (alreadySeen) return;

      this.shopLimitAlertVisible = true;
      try {
        await this.$store.dispatch('userSettings/save', {
          settings: {
            ...this.userSettings,
            notices: { shop_limit_reached_seen: true },
          },
          silent: true,
        });
      } catch (error) {
        console.warn('Impossible de mémoriser la notification de limite des sites', error);
      }
    },

    async fetchAccountingPlans() {
      if (!this.isGerant && !this.isSupport) return;

      try {
        const res = await this.$api.get('/accounting-plans');
        const plans = extractApiList(res.data)
          .filter((plan) => plan?.code === SYSCOHADA_REVISED_CODE)
          .map((plan) => ({
            code: plan.code,
            name: plan.name || DEFAULT_ACCOUNTING_PLAN.name,
          }));

        this.accountingPlans = plans.length ? plans : [DEFAULT_ACCOUNTING_PLAN];
      } catch (error) {
        console.warn('Impossible de charger les plans comptables', error);
        this.accountingPlans = [DEFAULT_ACCOUNTING_PLAN];
      }

      if (this.brandForm.accounting_plan !== SYSCOHADA_REVISED_CODE) {
        this.brandForm.accounting_plan = SYSCOHADA_REVISED_CODE;
      }
    },

    syncBrandFormFromMainSite() {
      const site = this.mainSite;
      if (!site) return;

      this.brandForm = {
        name: site.name || '',
        slogan: site.slogan || '',
        language: 'fr',
        accounting_plan: site.accounting_plan === SYSCOHADA_REVISED_CODE
          ? site.accounting_plan
          : SYSCOHADA_REVISED_CODE,
        logo_url: site.logo_url || site.logo || site.main_logo || '',
      };

      if (!this.brandLogoFile) {
        this.revokeBrandLogoPreview();
      }
    },

    onBrandLogoSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.brandLogoFile = file;
      this.revokeBrandLogoPreview();
      this.brandLogoPreviewUrl = URL.createObjectURL(file);
    },

    clearBrandLogoSelection() {
      this.brandLogoFile = null;
      this.revokeBrandLogoPreview();
      if (this.$refs.brandLogoInput) {
        this.$refs.brandLogoInput.value = '';
      }
    },

    revokeBrandLogoPreview() {
      if (this.brandLogoPreviewUrl) {
        try {
          URL.revokeObjectURL(this.brandLogoPreviewUrl);
        } catch (error) {
          console.warn('Impossible de libérer l’aperçu du logo', error);
        }
      }
      this.brandLogoPreviewUrl = '';
    },

    readBrandPreferences(siteId) {
      try {
        const raw = window.localStorage.getItem(BRAND_SETTINGS_STORAGE_KEY);
        const settings = raw ? JSON.parse(raw) : {};
        return settings[String(siteId)] || {};
      } catch (error) {
        console.warn('Préférences de marque illisibles', error);
        return {};
      }
    },

    writeBrandPreferences(siteId) {
      try {
        const raw = window.localStorage.getItem(BRAND_SETTINGS_STORAGE_KEY);
        const settings = raw ? JSON.parse(raw) : {};
        settings[String(siteId)] = {
          language: 'fr',
        };
        window.localStorage.setItem(BRAND_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      } catch (error) {
        console.warn('Préférences de marque non enregistrées', error);
      }
    },

    buildBrandPayload(site) {
      return {
        name: this.brandForm.name,
        slogan: this.brandForm.slogan || null,
        legal_name: site.legal_name || null,
        phone: site.phone || '',
        whatsapp_phone: site.whatsapp_phone || null,
        email: site.email || null,
        address: site.address || '',
        city: site.city || null,
        country: site.country || null,
        tax_id: site.tax_id || null,
        trade_register: site.trade_register || null,
        currency_code: site.currency_code || 'FCFA',
        logo_url: this.brandForm.logo_url || null,
        footer_note: site.footer_note || null,
        timezone: site.timezone || 'Africa/Libreville',
        tva_rate: site.tva_rate != null ? site.tva_rate : 18,
        css_rate: site.css_rate != null ? site.css_rate : 1,
        code: site.code || null,
        is_active: site.is_active !== undefined ? site.is_active : true,
        accounting_plan: SYSCOHADA_REVISED_CODE,
      };
    },

    appendBrandFormData(formData, payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else {
          formData.append(key, value == null ? '' : String(value));
        }
      });
    },

    async saveBrandSettings() {
      const site = this.mainSite;
      if (!site?.id) {
        this.errorMessage = 'Définissez d’abord un site principal.';
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingBrand = true;

      try {
        const payload = this.buildBrandPayload(site);
        const config = {
          headers: {
            'X-Shop-Id': String(site.id),
          },
        };

        if (this.brandLogoFile) {
          const formData = new FormData();
          this.appendBrandFormData(formData, payload);
          formData.append('logo', this.brandLogoFile);

          await this.$api.post('/shop-info', formData, {
            headers: {
              ...config.headers,
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          await this.$api.post('/shop-info', payload, config);
        }

        this.writeBrandPreferences(site.id);
        this.clearBrandLogoSelection();

        await this.fetchSites();

        if (this.currentShopId && Number(this.currentShopId) === Number(site.id)) {
          await this.$store.dispatch('shop/fetchInfo');
        }

        this.successMessage = 'Identité de marque enregistrée avec succès.';
      } catch (error) {
        console.error('Erreur enregistrement marque', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible d’enregistrer l’identité de marque.';
      } finally {
        this.isSavingBrand = false;
      }
    },

    openCreateDialog() {
      if (this.hasReachedShopLimit) {
        this.shopLimitAlertVisible = true;
        return;
      }

      this.dialogVisible = true;
    },

    openBrandSettingsDialog() {
      this.brandSettingsDialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    async handleSaved() {
      this.dialogVisible = false;
      this.successMessage = 'Site enregistré avec succès.';
      await this.fetchSites();
    },

    // ✅ helper : site actuel (connexion)
    isCurrentSite(site) {
      return Boolean(site && this.currentShopId && Number(site.id) === Number(this.currentShopId));
    },

    // ✅ helper : site principal
    isMainSite(site) {
      // adapte si ton champ s'appelle autrement (is_primary / is_main / is_principal)
      return !!site?.is_main;
    },

    siteTypeLabel(value) {
      const labels = {
        bars: 'Bars',
        epicerie: 'Épicerie',
        restaurant: 'Restaurant',
        boutique_en_ligne: 'Boutique en ligne',
        autres: 'Autres',
      };

      return labels[value] || 'Épicerie';
    },

    siteInitials(site) {
      const source = String(site?.name || site?.code || 'Site').trim();
      const words = source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);

      return (words.length ? words.map((word) => word[0]).join('') : 'ST').toUpperCase();
    },

    siteCoverClass(site) {
      return `site-cover-${site?.site_type || 'epicerie'}`;
    },

    siteTypeBadgeClass(site) {
      return `site-type-badge-${site?.site_type || 'epicerie'}`;
    },

    formatRatePercentage(value) {
      const numeric = Number(String(value ?? 0).replace(',', '.'));

      if (!Number.isFinite(numeric)) {
        return '0%';
      }

      const percentage = numeric > 0 && numeric < 1 ? numeric * 100 : numeric;
      const formatted = new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 2,
      }).format(percentage);

      return `${formatted}%`;
    },

    formatNumber(value) {
      const numeric = Number(value || 0);

      return new Intl.NumberFormat('fr-FR').format(Number.isFinite(numeric) ? numeric : 0);
    },

    formatCurrency(value) {
      const numeric = Number(value || 0);

      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF',
        maximumFractionDigits: 0,
      }).format(Number.isFinite(numeric) ? numeric : 0);
    },

    // ✅ action : définir principal
    async setMainSite(site) {
      if (!site?.id) return;
      if (!this.isGerant) return;
      if (!site.is_active) {
        alert("Impossible: le site doit être actif pour devenir principal.");
        return;
      }

      const ok = await this.$confirmAction(
        `Définir "${site.name}" comme site principal ?\n` +
        `Le logo global sera désormais celui du site principal (un seul logo pour la marque).`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        // ✅ endpoint à créer côté backend
        await this.$api.patch(`/shops/${site.id}/set-main`);

        this.successMessage = 'Site principal mis à jour.';
        await this.fetchSites();
      } catch (error) {
        console.error('Erreur set main site', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de définir le site principal.';
      } finally {
        this.isLoading = false;
      }
    },

    async switchToSite(site) {
      if (!site || !site.id) return;
      if (!this.canSwitchSite) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        await this.$store.dispatch('shop/changeSite', site.id);
        this.successMessage = 'Vous êtes maintenant connecté sur ce site.';
        this.$router.push({ name: 'Dashboard' });
      } catch (error) {
        console.error('Erreur changement de site', error);
        this.errorMessage = 'Impossible de basculer sur ce site.';
      } finally {
        this.isLoading = false;
      }
    },

    goToShow(row) {
      if (this.isGlobalUser) {
        if (this.canSwitchSite) {
          this.switchToSite(row);
        }
        return;
      }
      this.$router.push({ name: 'SiteShow', params: { id: row.id } });
    },

    siteShowHref(site) {
      if (!site?.id) return '#';

      if (this.isGlobalUser) {
        return this.canSwitchSite ? this.$router.resolve({ name: 'Dashboard' }).href : '#';
      }

      return this.$router.resolve({ name: 'SiteShow', params: { id: site.id } }).href;
    },
  },
};
</script>

<style scoped>
.bg-soft { background: var(--suite241-page-bg, var(--bs-body-bg)); }

.sites-page :deep(.btn-primary) {
  --bs-btn-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-hover-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-disabled-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-disabled-border-color: var(--suite241-admin-primary, #0d6efd);
}

.sites-page :deep(.btn-outline-primary) {
  --bs-btn-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-active-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
}

.sites-cards-wrap { width: 100%; max-width: 1200px; }

.brand-settings-form,
.site-card,
.new-site-card { border-radius: 0.5rem; }

.site-card,
.new-site-card {
  background-color: var(--suite241-card-bg, #ffffff);
  min-height: 390px;
}

.new-site-card-disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.new-site-card-disabled:hover,
.new-site-card-disabled:focus-visible {
  transform: none;
}

.site-card {
  border: 1px solid var(--suite241-card-border, #e5e7eb);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.site-card:hover,
.site-card:focus-within {
  border-color: var(--suite241-admin-primary, #0d6efd);
  box-shadow: 0 0.65rem 1.5rem rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.14) !important;
  transform: translateY(-2px);
}

.site-card-current {
  border-color: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.72);
  box-shadow: 0 0 0 1px rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.12), 0 0.35rem 1rem rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.1) !important;
}

.site-card-current:hover,
.site-card-current:focus-within {
  border-color: var(--suite241-admin-primary-hover, #0b5ed7);
  box-shadow: 0 0 0 3px rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.18), 0 0.75rem 1.65rem rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.2) !important;
}

.site-card-link {
  color: inherit;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  text-decoration: none;
}

.site-card-link:focus-visible {
  outline: 3px solid rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.35);
  outline-offset: -3px;
}

.site-card-footer {
  background: var(--suite241-card-bg, #ffffff);
  border-top: 1px solid var(--suite241-card-border, #e5e7eb);
  padding: 0.75rem 1rem;
}

.site-card-facts {
  font-size: 0.72rem;
  line-height: 1.15;
}

.site-card-facts > div {
  gap: 0.75rem;
  margin-bottom: 0.2rem !important;
}

.site-card-facts .fw-semibold {
  overflow-wrap: anywhere;
  text-align: right;
}

.site-card .btn { border-radius: 0.5rem; }

.site-cover {
  align-items: center;
  color: #fff;
  display: flex;
  height: 132px;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.site-cover-image {
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
  width: 100%;
}

.site-card:hover .site-cover-image,
.site-card:focus-within .site-cover-image {
  transform: scale(1.07);
}

@media (prefers-reduced-motion: reduce) {
  .site-card,
  .site-cover-image {
    transition: none;
  }

  .site-card:hover,
  .site-card:focus-within {
    transform: none;
  }

  .site-card:hover .site-cover-image,
  .site-card:focus-within .site-cover-image {
    transform: none;
  }
}

.site-cover-initials {
  align-items: center;
  display: flex;
  font-size: 2.25rem;
  font-weight: 800;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.site-cover-bars {
  background: linear-gradient(135deg, #203a43, #2c5364);
}

.site-cover-epicerie {
  background: linear-gradient(135deg, #2f6f4e, #d7a431);
}

.site-cover-restaurant {
  background: linear-gradient(135deg, #7f1d1d, #c7772f);
}

.site-cover-boutique_en_ligne {
  background: linear-gradient(135deg, #1d4ed8, #06b6d4);
}

.site-cover-autres {
  background: linear-gradient(135deg, #475569, #94a3b8);
}

.site-type-badge {
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.4rem 0.65rem;
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
}

.site-type-badge-bars { color: #203a43; }
.site-type-badge-epicerie { color: #2f6f4e; }
.site-type-badge-restaurant { color: #7f1d1d; }
.site-type-badge-boutique_en_ligne { color: #1d4ed8; }
.site-type-badge-autres { color: #475569; }

.new-site-card {
  align-items: center;
  background: var(--suite241-admin-accent-soft, rgba(13, 110, 253, 0.04));
  border: 2px dashed rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.35);
  color: var(--suite241-admin-primary, var(--bs-primary));
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.new-site-card:hover,
.new-site-card:focus-visible {
  background: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.08);
  border-color: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.7);
  transform: translateY(-1px);
}

.new-site-card-icon {
  align-items: center;
  background: var(--suite241-admin-primary, var(--bs-primary));
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 1.25rem;
  height: 3rem;
  justify-content: center;
  width: 3rem;
}

.new-site-card-title {
  color: #172033;
  font-weight: 700;
}

.new-site-card-text {
  color: #6c757d;
  font-size: 0.875rem;
  line-height: 1.35;
}

.brand-logo-panel {
  align-items: center;
  background: #f8f9fa;
  border: 1px dashed rgba(33, 37, 41, 0.2);
  border-radius: 1rem;
  display: flex;
  height: 132px;
  justify-content: center;
  overflow: hidden;
}

.brand-logo-preview {
  height: 100%;
  object-fit: contain;
  padding: 1rem;
  width: 100%;
}

.brand-logo-placeholder {
  align-items: center;
  color: #6c757d;
  display: flex;
  font-size: 2rem;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.bg-success-subtle {
  background: rgba(25, 135, 84, 0.12);
}
</style>
